import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const WeightLossPage = () => {
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    currentWeight: '',
    targetWeight: '',
    medicalHistory: '',
    paymentType: '',
    insuranceProvider: '',
    policyNumber: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    billingAddress: ''
  });

  const [submitStatus, setSubmitStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus(null);

    try {
      const formDataEncoded = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataEncoded.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d590homisj01hmefr0f0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataEncoded.toString()
      });

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Payment information submitted successfully! Our team will contact you within 24 hours to schedule your initial consultation.' });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: '',
          currentWeight: '',
          targetWeight: '',
          medicalHistory: '',
          paymentType: '',
          insuranceProvider: '',
          policyNumber: '',
          cardNumber: '',
          expiryDate: '',
          cvv: '',
          billingAddress: ''
        });
      } else {
        setSubmitStatus({ type: 'error', message: 'Submission failed. Please try again.' });
      }
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Network error. Please try again.' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center bg-gradient-to-r from-primary to-primary-dark">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <motion.h1
            className="text-5xl md:text-6xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Weight Loss Program
          </motion.h1>
          <motion.p
            className="text-white/90 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link> <span className="mx-2">/</span> Weight Loss
          </motion.p>
        </div>
      </section>

      {!showPaymentForm ? (
        <>
          {/* Program Overview */}
          <section className="py-20 bg-beige-light">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-neutral-gray mb-6">Evidence-Based Weight Management</h2>
                <p className="text-xl text-neutral-gray/70 max-w-3xl mx-auto">
                  Our comprehensive weight loss program is provider-supervised and tailored to your unique health needs, combining medical expertise with behavioral support.
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mb-16">
                <motion.div
                  className="bg-white rounded-xl p-8 shadow-lg border border-gray-100"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-heart-pulse-line text-3xl text-primary"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-gray mb-4">Medical Supervision</h3>
                  <p className="text-neutral-gray/70 leading-relaxed">
                    Provider-supervised program with regular monitoring, health assessments, and medication management when appropriate.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white rounded-xl p-8 shadow-lg border border-gray-100"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-restaurant-line text-3xl text-primary"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-gray mb-4">Nutrition &amp; Exercise</h3>
                  <p className="text-neutral-gray/70 leading-relaxed">
                    Personalized diet plans and exercise routines designed for your lifestyle, preferences, and health conditions.
                  </p>
                </motion.div>

                <motion.div
                  className="bg-white rounded-xl p-8 shadow-lg border border-gray-100"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                    <i className="ri-brain-line text-3xl text-primary"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-gray mb-4">Behavioral Support</h3>
                  <p className="text-neutral-gray/70 leading-relaxed">
                    Mental health support addressing emotional eating, motivation, and sustainable behavioral change for long-term success.
                  </p>
                </motion.div>
              </div>

              {/* Program Components */}
              <div className="bg-white rounded-2xl p-10 mb-16 shadow-sm">
                <h3 className="text-3xl font-bold text-neutral-gray mb-8 text-center">What's Included</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-neutral-gray mb-2">Initial Medical Evaluation</h4>
                      <p className="text-neutral-gray/70 text-sm">Comprehensive health assessment, lab work review, and personalized treatment plan development</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-neutral-gray mb-2">Customized Meal Plans</h4>
                      <p className="text-neutral-gray/70 text-sm">Evidence-based nutrition guidance tailored to your dietary needs and preferences</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-neutral-gray mb-2">Exercise Programming</h4>
                      <p className="text-neutral-gray/70 text-sm">Personalized fitness plans appropriate for your current fitness level and health status</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-neutral-gray mb-2">Medication Management (If Needed)</h4>
                      <p className="text-neutral-gray/70 text-sm">FDA-approved weight loss medications when medically appropriate and beneficial</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-neutral-gray mb-2">Mental Health Support</h4>
                      <p className="text-neutral-gray/70 text-sm">Behavioral therapy addressing emotional eating, stress management, and motivation</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-xl text-white"></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-neutral-gray mb-2">Regular Follow-ups</h4>
                      <p className="text-neutral-gray/70 text-sm">Ongoing monitoring, progress tracking, and plan adjustments for optimal results</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mental Health Section */}
              <motion.div
                className="bg-white rounded-2xl p-10 shadow-lg border border-gray-100 mb-16"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-3xl font-bold text-neutral-gray mb-6">The Mental Health Connection</h3>
                <p className="text-lg text-neutral-gray/70 leading-relaxed mb-6">
                  Successful weight loss isn't just about diet and exercise—it requires addressing the psychological factors that influence eating behaviors and lifestyle choices. Our integrated approach includes:
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-primary text-xl mt-1"></i>
                      <span className="text-neutral-gray/70">Cognitive behavioral strategies for sustainable change</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-primary text-xl mt-1"></i>
                      <span className="text-neutral-gray/70">Stress and emotional eating management</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-primary text-xl mt-1"></i>
                      <span className="text-neutral-gray/70">Body image and self-esteem support</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-primary text-xl mt-1"></i>
                      <span className="text-neutral-gray/70">Motivation and goal-setting techniques</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-primary text-xl mt-1"></i>
                      <span className="text-neutral-gray/70">Relapse prevention strategies</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <i className="ri-arrow-right-s-line text-primary text-xl mt-1"></i>
                      <span className="text-neutral-gray/70">Treatment of co-occurring mental health conditions</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Evidence-Based Approach */}
              <div className="bg-white rounded-2xl p-10 mb-16 shadow-sm">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-award-line text-3xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-neutral-gray mb-4">Evidence-Based &amp; Provider-Supervised</h3>
                    <p className="text-neutral-gray/70 leading-relaxed mb-4">
                      Our weight loss program is grounded in scientific research and clinical best practices. Every aspect of your treatment plan is supervised by experienced healthcare providers who specialize in weight management and mental health.
                    </p>
                    <p className="text-neutral-gray/70 leading-relaxed">
                      We use proven interventions that have demonstrated effectiveness in clinical studies, ensuring you receive safe, effective care that produces lasting results.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA */}
              <motion.div
                className="bg-gradient-to-r from-primary to-primary-dark rounded-2xl p-12 text-white text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-3xl font-bold mb-4">Ready to Start Your Weight Loss Journey?</h3>
                <p className="text-white/90 mb-8 text-lg max-w-2xl mx-auto">
                  Take the first step toward a healthier you with our comprehensive, medically supervised weight loss program.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    onClick={() => setShowPaymentForm(true)}
                    className="bg-white text-primary px-10 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-file-list-3-line mr-2"></i>
                    Enroll in Program
                  </button>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                      href="/appointment"
                      className="px-8 py-3 bg-[rgba(75,162,75)] text-white rounded-md hover:bg-[rgba(75,162,75,0.9)] transition-colors font-medium whitespace-nowrap inline-flex items-center justify-center gap-2"
                    >
                      <i className="ri-calendar-line"></i>
                      Schedule Consultation
                    </a>
                    <a
                      href="/payment-setup"
                      className="px-8 py-3 bg-white text-[rgba(75,162,75)] border-2 border-[rgba(75,162,75)] rounded-md hover:bg-[rgba(75,162,75)] hover:text-white transition-colors font-medium whitespace-nowrap inline-flex items-center justify-center gap-2"
                    >
                      <i className="ri-secure-payment-line"></i>
                      Set Up Payment
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </>
      ) : (
        <section className="py-20 bg-beige-light">
          <div className="max-w-4xl mx-auto px-6">
            <button
              onClick={() => {
                setShowPaymentForm(false);
                setSubmitStatus(null);
              }}
              className="mb-8 text-primary hover:text-primary-dark font-semibold flex items-center gap-2 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Program Information
            </button>

            <div className="bg-white rounded-xl shadow-lg p-10">
              <h2 className="text-3xl font-bold text-neutral-gray mb-6">Weight Loss Program Enrollment</h2>
              <p className="text-neutral-gray/70 mb-8">Complete this form to enroll in our weight loss program and set up your payment information.</p>

              {submitStatus && (
                <div className={`mb-6 p-4 rounded-lg ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  <div className="flex items-center gap-2">
                    <i className={`${submitStatus.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'} text-xl`}></i>
                    <span>{submitStatus.message}</span>
                  </div>
                </div>
              )}

              <form id="weight-loss-payment-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-bold text-neutral-gray mb-4">Personal Information</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-neutral-gray mb-2">First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        required
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-neutral-gray mb-2">Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        required
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-neutral-gray mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-gray mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-neutral-gray mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      required
                      value={formData.dateOfBirth}
                      onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-gray mb-2">Current Weight (lbs) *</label>
                    <input
                      type="number"
                      name="currentWeight"
                      required
                      value={formData.currentWeight}
                      onChange={(e) => setFormData({ ...formData, currentWeight: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-gray mb-2">Target Weight (lbs) *</label>
                    <input
                      type="number"
                      name="targetWeight"
                      required
                      value={formData.targetWeight}
                      onChange={(e) => setFormData({ ...formData, targetWeight: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-gray mb-2">Medical History *</label>
                  <textarea
                    name="medicalHistory"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.medicalHistory}
                    onChange={(e) => setFormData({ ...formData, medicalHistory: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder="Please list any medical conditions, medications, or allergies (max 500 characters)"
                  ></textarea>
                  <p className="text-sm text-neutral-gray/60 mt-1">{formData.medicalHistory.length}/500 characters</p>
                </div>

                {/* Payment Information */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-xl font-bold text-neutral-gray mb-4">Payment Information</h3>
                  
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-neutral-gray mb-2">Payment Type *</label>
                    <select
                      name="paymentType"
                      required
                      value={formData.paymentType}
                      onChange={(e) => setFormData({ ...formData, paymentType: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select Payment Type</option>
                      <option value="Insurance">Insurance</option>
                      <option value="Self-Pay">Self-Pay / No Insurance</option>
                    </select>
                  </div>

                  {formData.paymentType === 'Insurance' && (
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-semibold text-neutral-gray mb-2">Insurance Provider *</label>
                        <select
                          name="insuranceProvider"
                          required
                          value={formData.insuranceProvider}
                          onChange={(e) => setFormData({ ...formData, insuranceProvider: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                          <option value="">Select Insurance</option>
                          <option value="Optum">Optum</option>
                          <option value="Aetna">Aetna</option>
                          <option value="Cigna">Cigna</option>
                          <option value="Medicaid">Medicaid</option>
                          <option value="Medicare">Medicare</option>
                          <option value="Colorado Access">Colorado Access</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-neutral-gray mb-2">Policy Number *</label>
                        <input
                          type="text"
                          name="policyNumber"
                          required
                          value={formData.policyNumber}
                          onChange={(e) => setFormData({ ...formData, policyNumber: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  <div className="bg-primary/5 rounded-lg p-6 border border-primary/20 mb-6">
                    <h4 className="font-bold text-neutral-gray mb-3">Credit Card on File</h4>
                    <p className="text-sm text-neutral-gray/70 mb-4">We require a credit card on file for copays, deductibles, or self-pay services.</p>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-neutral-gray mb-2">Card Number *</label>
                        <input
                          type="text"
                          name="cardNumber"
                          required
                          value={formData.cardNumber}
                          onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="1234 5678 9012 3456"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-neutral-gray mb-2">Expiry Date *</label>
                          <input
                            type="text"
                            name="expiryDate"
                            required
                            value={formData.expiryDate}
                            onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="MM/YY"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-neutral-gray mb-2">CVV *</label>
                          <input
                            type="text"
                            name="cvv"
                            required
                            value={formData.cvv}
                            onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="123"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-neutral-gray mb-2">Billing Address *</label>
                        <input
                          type="text"
                          name="billingAddress"
                          required
                          value={formData.billingAddress}
                          onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          placeholder="Street, City, State, ZIP"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-primary text-white py-4 rounded-lg font-semibold hover:bg-primary-dark transition-all shadow-lg whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-fill mr-2"></i>
                  Submit Enrollment
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default WeightLossPage;